import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Empty,
  Flex,
  Grid,
  Modal,
  Segmented,
  Select,
  Spin,
  Typography,
} from "antd";
import { ArrowLeftOutlined, FilterOutlined } from "@ant-design/icons";
import { Card } from "@rumbledirect/ui";
import { ChatConversationStatus } from "@rumble-it/common-lib/types/models/ChatConversation.interface";
import { usePageTitle } from "../../../lib/hooks/usePageTitle";
import { useChatConversations } from "../../../lib/hooks/useChat";
import { ChatConversationFilters } from "../../../lib/api/resources/chat";
import {
  chatChannelConfig,
  chatConversationStatusConfig,
} from "./data/chatChannelConfig";
import { InboxScope, scopeToFilters } from "./data/chatInbox";
import { ChatConversationView } from "./components/ChatConversationView";
import { ChatConversationListItem } from "./components/ChatConversationListItem";

const { Text } = Typography;

export const ChatInbox = () => {
  usePageTitle("Berichten");
  const screens = Grid.useBreakpoint();
  const isWide = screens.md ?? true;

  const [searchParams, setSearchParams] = useSearchParams();
  const activeConversationId = searchParams.get("conversation");

  const setActiveConversationId = (id: string | null) => {
    setSearchParams(
      (params) => {
        if (id) {
          params.set("conversation", id);
        } else {
          params.delete("conversation");
        }
        return params;
      },
      { replace: true },
    );
  };

  const [scope, setScope] = useState<InboxScope>("unhandled");
  const [status, setStatus] = useState<ChatConversationStatus | undefined>(
    undefined,
  );
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filters: ChatConversationFilters = {
    ...scopeToFilters(scope),
    ...(status ? { status } : {}),
    channel: "whatsapp",
  };

  const conversationsQuery = useChatConversations(filters);
  const conversations = conversationsQuery.data?.member ?? [];

  const filterCount = status ? 1 : 0;

  const listPanel = (
    <Card
      title="Berichten"
      className="h-full"
      extra={
        <Badge count={filterCount} size="small">
          <Button
            type="text"
            icon={<FilterOutlined />}
            onClick={() => setFiltersOpen(true)}
          />
        </Badge>
      }
    >
      <div className="mb-3">
        <Segmented<InboxScope>
          block
          value={scope}
          onChange={setScope}
          options={[
            { label: "Nieuw", value: "unhandled" },
            { label: "Openstaand", value: "all" },
            { label: "Archief", value: "archived" },
          ]}
        />
      </div>

      <div className="-mx-6 -mb-6 border-t">
        {conversationsQuery.isLoading ? (
          <Flex justify="center" style={{ padding: 24 }}>
            <Spin />
          </Flex>
        ) : conversations.length === 0 ? (
          <Empty description="Geen gesprekken" style={{ padding: 24 }} />
        ) : (
          conversations.map((conversation) => (
            <ChatConversationListItem
              key={conversation.id}
              conversation={conversation}
              isActive={conversation.id === activeConversationId}
              onClick={() => setActiveConversationId(conversation.id)}
            />
          ))
        )}
      </div>
    </Card>
  );

  const detailPanel = (
    <Card
      className="h-full"
      styles={{ body: { height: "calc(100vh - 200px)", minHeight: 400 } }}
    >
      {activeConversationId ? (
        <Flex vertical style={{ height: "100%" }} gap={8}>
          {!isWide ? (
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              style={{ alignSelf: "flex-start" }}
              onClick={() => setActiveConversationId(null)}
            >
              Terug
            </Button>
          ) : null}
          <div style={{ flex: 1, minHeight: 0 }}>
            <ChatConversationView conversationId={activeConversationId} />
          </div>
        </Flex>
      ) : (
        <Flex align="center" justify="center" style={{ height: "100%" }}>
          <Empty description="Selecteer een gesprek" />
        </Flex>
      )}
    </Card>
  );

  return (
    <>
      {isWide ? (
        <Flex gap={16} className="w-full !px-0">
          <div style={{ width: 320, flexShrink: 0 }}>{listPanel}</div>
          <div style={{ flex: 1, minWidth: 0 }}>{detailPanel}</div>
        </Flex>
      ) : (
        <div className="w-full !px-0">
          {activeConversationId ? detailPanel : listPanel}
        </div>
      )}

      <Modal
        title="Filters"
        open={filtersOpen}
        onCancel={() => setFiltersOpen(false)}
        onOk={() => setFiltersOpen(false)}
        okText="Toepassen"
        footer={[
          <Button
            key="reset"
            onClick={() => {
              setStatus(undefined);
            }}
          >
            Wissen
          </Button>,
          <Button key="ok" type="primary" onClick={() => setFiltersOpen(false)}>
            Toepassen
          </Button>,
        ]}
      >
        <Flex vertical gap={12} style={{ marginTop: 12 }}>
          <Flex vertical gap={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Kanaal
            </Text>
            <Select
              value="whatsapp"
              disabled
              options={[
                {
                  value: "whatsapp",
                  label: (
                    <Flex align="center" gap={6}>
                      {chatChannelConfig.whatsapp.icon}
                      {chatChannelConfig.whatsapp.label}
                    </Flex>
                  ),
                },
              ]}
            />
          </Flex>
          <Flex vertical gap={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Status
            </Text>
            <Select
              allowClear
              placeholder="Alle statussen"
              value={status}
              onChange={(value) => setStatus(value)}
              options={Object.entries(chatConversationStatusConfig).map(
                ([value, config]) => ({ value, label: config.label }),
              )}
            />
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};
